import { Box } from '@mui/material';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import colors from '../../../../assets/theme/base/colors';

function getBarColour(severity: SeverityLevel): { border: string; bg: string; iconBg: string; text: string } {
    const s = colors.deviationSeverity;
    if (severity === SeverityLevel.OverRepresented) { return { border: s.overRepresented.bgHover, bg: s.overRepresented.bg, iconBg: s.overRepresented.iconBg, text: s.overRepresented.icon }; }
    if (severity === SeverityLevel.OnTrack)         { return { border: s.onTrack.bgHover,         bg: s.onTrack.bg,         iconBg: s.onTrack.iconBg,         text: s.onTrack.icon }; }
    if (severity === SeverityLevel.Medium)          { return { border: s.medium.bgHover,          bg: s.medium.bg,          iconBg: s.medium.iconBg,          text: s.medium.icon }; }
    if (severity === SeverityLevel.High)            { return { border: s.high.bgHover,            bg: s.high.bg,            iconBg: s.high.iconBg,            text: s.high.icon }; }
    return { border: s.critical.bgHover, bg: s.critical.bg, iconBg: s.critical.iconBg, text: s.critical.icon };
}

const severityLabels: Record<number, string> = {
    [SeverityLevel.Critical]:        'Critical',
    [SeverityLevel.High]:            'High',
    [SeverityLevel.Medium]:          'Medium',
    [SeverityLevel.OnTrack]:         'On Track',
    [SeverityLevel.OverRepresented]: 'Over-represented',
};

interface DesignatedDisabledBarProps {
    rowHeaderWidth: number;
    data: DeviationsDto.HeatmapDesignatedDisabledDto;
}

export function DesignatedDisabledBar({ rowHeaderWidth, data }: DesignatedDisabledBarProps) {
    const currentPercent = data.currentPercentage;
    const targetPercent = data.targetPercentage;
    const currentHeadcount = data.designatedHeadcount;
    const targetHeadcount = Math.round((data.targetPercentage ?? 0) / 100 * data.totalHeadcount);
    const relativeGap = data.relativeGapPercentage ?? null;
    const severityLabel = severityLabels[data.severity] ?? 'Unknown';
    const styles = getBarColour(data.severity);

    let Icon;
    if (data.severity === SeverityLevel.OverRepresented) {
        Icon = TrendingUpIcon;
    } else if (data.severity === SeverityLevel.OnTrack) {
        Icon = CheckCircleOutlinedIcon;
    } else if (data.severity === SeverityLevel.Medium) {
        Icon = InfoOutlinedIcon;
    } else if (data.severity === SeverityLevel.High) {
        Icon = WarningAmberOutlinedIcon;
    } else {
        Icon = DisabledByDefaultOutlinedIcon;
    }

    return (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'stretch', gap: 2 }}>
            <Box
                sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: colors.dark.main,
                    width: rowHeaderWidth,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1.25,
                }}
            >
                Designated Disabled Employees
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    borderRadius: '4px',
                    border: `1px solid ${styles.border}`,
                    backgroundColor: styles.bg,
                    overflow: 'hidden',
                    minHeight: '60px',
                    flex: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        flexShrink: 0,
                        backgroundColor: styles.iconBg,
                    }}
                >
                    <Icon sx={{ width: 16, height: 16, color: colors.white.main }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2, py: 1, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <Box component="span" sx={{ fontSize: '1.125rem', fontWeight: 700, color: styles.text }}>
                            {(currentPercent ?? 0).toFixed(1)}%
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.75rem', color: styles.text, opacity: 0.7 }}>
                            ({currentHeadcount} employees)
                        </Box>
                    </Box>
                    <Box component="span" sx={{ fontSize: '0.75rem', color: styles.text, opacity: 0.7 }}>vs</Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: styles.text }}>
                            {targetPercent ?? 0}% target
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.75rem', color: styles.text, opacity: 0.7 }}>
                            ({targetHeadcount} employees)
                        </Box>
                    </Box>
                    {relativeGap != null && (
                        <>
                            <Box sx={{ width: '1px', height: 20, backgroundColor: styles.text, opacity: 0.2, flexShrink: 0 }} />
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 700, color: styles.text }}>
                                    {relativeGap > 0 ? '+' : ''}{relativeGap.toFixed(1)}%
                                </Box>
                                <Box component="span" sx={{ fontSize: '0.75rem', color: styles.text, opacity: 0.7 }}>
                                    relative gap
                                </Box>
                            </Box>
                        </>
                    )}
                    <Box sx={{ width: '1px', height: 20, backgroundColor: styles.text, opacity: 0.2, flexShrink: 0 }} />
                    <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: styles.text }}>
                        {severityLabel}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
